import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2,
  ArrowLeft,
  Upload,
  X,
  Send,
  Paperclip,
  AlertCircle,
} from "lucide-react";
import { SUPPORT } from "@/constants/testIds";
import { useLang } from "@/lib/useLang";
import { PortalHeader, PortalFooter } from "@/components/PortalChrome";
import { supportApi } from "@/lib/api";

const STATUS_TONE = {
  submitted: "bg-[#F0E9DC] text-[#0B1930]",
  reviewing: "bg-[#F0E9DC] text-[#0B1930]",
  scheduled: "bg-[#DDF3E4] text-[#0F5132]",
  resolved: "bg-[#DDF3E4] text-[#0F5132]",
  closed: "bg-[#EDE5D4] text-[#4B5563]",
};

export default function SupportRequestDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { lang, t, toggle } = useLang();

  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [followMessage, setFollowMessage] = useState("");
  const [followPhoto, setFollowPhoto] = useState(null);
  const [followSubmitting, setFollowSubmitting] = useState(false);
  const [followError, setFollowError] = useState("");

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await supportApi.get(requestId);
      setState({ loading: false, data, error: "" });
    } catch (err) {
      if (err?.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      setState({
        loading: false,
        data: null,
        error: err?.detail || "Unable to load this request.",
      });
    }
  }, [requestId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Live status polling from the Hub. Every 30s while the tab is visible.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await supportApi.liveStatus(requestId);
        if (cancelled || !data) return;
        setState((s) => {
          if (!s.data) return s;
          // Merge server-live status + updates into the local view.
          const seenLocal = new Set(
            (s.data.updates || []).map((u) => u.update_id),
          );
          const mergedUpdates = [...(s.data.updates || [])];
          for (const u of data.updates || []) {
            const key = u.update_id || `${u.updated_at}-${u.author_type}`;
            if (!seenLocal.has(key)) {
              mergedUpdates.push({
                update_id: key,
                author_type: u.author_type || "staff",
                message: u.message || "",
                attachments: u.attachments || [],
                created_at: u.updated_at || u.created_at,
              });
            }
          }
          return {
            ...s,
            data: {
              ...s.data,
              status: data.status || s.data.status,
              updates: mergedUpdates,
            },
          };
        });
      } catch {
        /* silent — poller keeps trying */
      }
      if (!cancelled && document.visibilityState === "visible") {
        timer = setTimeout(tick, 30_000);
      }
    };
    const startIfVisible = () => {
      if (document.visibilityState === "visible") {
        timer = setTimeout(tick, 30_000);
      }
    };
    document.addEventListener("visibilitychange", startIfVisible);
    startIfVisible();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", startIfVisible);
    };
  }, [requestId]);

  const onSubmitFollow = useCallback(
    async (e) => {
      e.preventDefault();
      if (!followMessage.trim()) return;
      setFollowSubmitting(true);
      setFollowError("");
      try {
        const form = new FormData();
        form.append("message", followMessage.trim());
        if (followPhoto) form.append("photo", followPhoto);
        await supportApi.addUpdate(requestId, form);
        setFollowMessage("");
        setFollowPhoto(null);
        await load();
      } catch (err) {
        setFollowError(err?.detail || t.support.errorGeneric);
      } finally {
        setFollowSubmitting(false);
      }
    },
    [followMessage, followPhoto, requestId, load, t.support.errorGeneric],
  );

  if (state.loading) {
    return (
      <div
        className="min-h-screen flex flex-col zmex-page-bg"
        data-testid={SUPPORT.detailPage}
      >
        <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-[#4B5563]" />
        </main>
        <PortalFooter t={t} />
      </div>
    );
  }

  if (!state.data) {
    return (
      <div
        className="min-h-screen flex flex-col zmex-page-bg"
        data-testid={SUPPORT.detailPage}
      >
        <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <p className="text-sm text-red-600">{state.error || "Not found."}</p>
            <button
              type="button"
              onClick={() => navigate("/portal/support/requests")}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#B8925A] hover:text-[#0B1930]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t.support.backToRequests}
            </button>
          </div>
        </main>
        <PortalFooter t={t} />
      </div>
    );
  }

  const r = state.data;

  return (
    <div
      className="min-h-screen flex flex-col zmex-page-bg"
      data-testid={SUPPORT.detailPage}
    >
      <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-16">
          <button
            type="button"
            onClick={() => navigate("/portal/support/requests")}
            className="inline-flex items-center gap-1.5 text-sm text-[#4B5563] hover:text-[#0B1930] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t.support.backToRequests}
          </button>

          <div className="mt-6 rounded-3xl bg-white border border-[#EDE5D4] p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                  STATUS_TONE[r.status] || STATUS_TONE.submitted
                }`}
                data-testid={SUPPORT.detailStatus}
              >
                {t.support.statusValues[r.status] || r.status}
              </span>
              <span
                className="text-xs font-mono text-[#4B5563]"
                data-testid={SUPPORT.detailConfirmation}
              >
                {r.confirmation_number}
              </span>
              <span className="text-xs text-[#4B5563]">
                {t.support.categoryLabels[r.category]}
              </span>
            </div>

            <h1 className="mt-3 font-heading uppercase tracking-tight text-[#0B1930] text-2xl sm:text-3xl">
              {r.subject}
            </h1>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                  {t.support.confirmationSubmittedLabel}
                </dt>
                <dd className="mt-0.5 text-[#0B1930]">
                  {new Date(r.created_at).toLocaleString(
                    lang === "es" ? "es-US" : "en-US",
                    { dateStyle: "medium", timeStyle: "short" },
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                  {t.support.confirmationCategoryLabel}
                </dt>
                <dd className="mt-0.5 text-[#0B1930]">
                  {r.account_display_name}
                </dd>
              </div>
              {r.location_label && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                    {t.support.confirmationLocationLabel}
                  </dt>
                  <dd className="mt-0.5 text-[#0B1930]">{r.location_label}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                  {t.dashboard.contactInfo}
                </dt>
                <dd className="mt-0.5 text-[#0B1930]">
                  {r.contact_display_masked}
                </dd>
              </div>
            </dl>

            <div className="mt-6 whitespace-pre-wrap text-[15px] text-[#0B1930] leading-relaxed">
              {r.message}
            </div>

            {r.attachments && r.attachments.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#4B5563] mb-2">
                  {t.support.detailAttachments}
                </p>
                <AttachmentList attachments={r.attachments} />
              </div>
            )}
          </div>

          {/* Updates timeline */}
          <div className="mt-8">
            <h2 className="font-heading uppercase tracking-wide text-[#0B1930] text-lg">
              {t.support.detailUpdates}
            </h2>
            {(!r.updates || r.updates.length === 0) && (
              <p className="mt-2 text-sm text-[#4B5563]">
                {t.support.detailNoUpdates}
              </p>
            )}
            {r.updates && r.updates.length > 0 && (
              <ul className="mt-4 space-y-4">
                {r.updates.map((u) => (
                  <li
                    key={u.update_id}
                    className="rounded-2xl bg-white border border-[#EDE5D4] p-4 sm:p-5"
                  >
                    <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                      <span className="font-semibold text-[#0B1930]">
                        {u.author_type === "staff"
                          ? "ZMEX Team"
                          : "You"}
                      </span>
                      <span>·</span>
                      <span>
                        {new Date(u.created_at).toLocaleString(
                          lang === "es" ? "es-US" : "en-US",
                          { dateStyle: "medium", timeStyle: "short" },
                        )}
                      </span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-[#0B1930]">
                      {u.message}
                    </p>
                    {u.attachments && u.attachments.length > 0 && (
                      <div className="mt-3">
                        <AttachmentList attachments={u.attachments} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Follow-up form */}
          <div className="mt-10">
            <h2 className="font-heading uppercase tracking-wide text-[#0B1930] text-lg">
              {t.support.detailFollowUpHeading}
            </h2>
            <p className="mt-1 text-xs text-[#4B5563]">
              {t.support.detailFollowUpSub}
            </p>
            {r.can_add_updates ? (
              <form
                onSubmit={onSubmitFollow}
                className="mt-4 rounded-2xl bg-white border border-[#EDE5D4] p-4 sm:p-5"
                data-testid={SUPPORT.followUpForm}
                encType="multipart/form-data"
              >
                <textarea
                  value={followMessage}
                  onChange={(e) => setFollowMessage(e.target.value)}
                  rows={4}
                  maxLength={4000}
                  placeholder={t.support.detailFollowUpPlaceholder}
                  className="w-full rounded-xl border border-[#E6E0D1] bg-white px-3 py-3 text-[15px] text-[#0B1930] outline-none focus:border-[#C8A46B] focus:ring-2 focus:ring-[#C8A46B]/25"
                  data-testid={SUPPORT.followUpMessage}
                />
                <div className="mt-3">
                  {followPhoto ? (
                    <div className="flex items-center gap-3 rounded-xl border border-[#E6E0D1] bg-[#F5F1EA] px-3 py-2">
                      <Paperclip className="h-4 w-4 text-[#0B1930]" />
                      <span className="text-xs text-[#0B1930] truncate flex-1">
                        {followPhoto.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setFollowPhoto(null)}
                        className="text-[#4B5563] hover:text-[#0B1930]"
                        aria-label="Remove"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#B8925A] hover:text-[#0B1930] cursor-pointer">
                      <Upload className="h-3.5 w-3.5" />
                      Attach photo
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                        className="hidden"
                        data-testid={SUPPORT.followUpPhoto}
                        onChange={(e) =>
                          setFollowPhoto(e.target.files?.[0] || null)
                        }
                      />
                    </label>
                  )}
                </div>
                {followError && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
                    <span>{followError}</span>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={followSubmitting || !followMessage.trim()}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0B1930] hover:bg-[#162B4D] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-70"
                  data-testid={SUPPORT.followUpSubmitBtn}
                >
                  {followSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t.support.detailFollowUpSubmitting}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      {t.support.detailFollowUpSubmit}
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="mt-4 rounded-2xl bg-white border border-dashed border-[#EDE5D4] p-4 text-sm text-[#4B5563]">
                {t.support.detailFollowUpClosed}
              </div>
            )}
          </div>
        </div>
      </main>
      <PortalFooter t={t} />
    </div>
  );
}

function AttachmentList({ attachments }) {
  const url = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/portal/support/attachments/${id}`;
  return (
    <div className="flex flex-wrap gap-3">
      {attachments.map((a) => (
        <a
          key={a.attachment_id}
          href={url(a.attachment_id)}
          target="_blank"
          rel="noreferrer"
          className="group block h-24 w-24 rounded-xl overflow-hidden border border-[#EDE5D4] bg-[#F5F1EA]"
        >
          <img
            src={url(a.attachment_id)}
            alt={a.original_filename || "attachment"}
            className="h-full w-full object-cover group-hover:opacity-90 transition-opacity"
          />
        </a>
      ))}
    </div>
  );
}
